import type { PullRequestReaction, PullRequestReactionContent } from "@afar-research/contracts";

/** The picker's order, which is GitHub's: the two verdicts first, then the rest as it lists them. */
export const PULL_REQUEST_REACTION_ORDER: ReadonlyArray<PullRequestReactionContent> = [
  "THUMBS_UP",
  "THUMBS_DOWN",
  "LAUGH",
  "HOORAY",
  "CONFUSED",
  "HEART",
  "ROCKET",
  "EYES",
];

const REACTION_EMOJI: Record<PullRequestReactionContent, string> = {
  THUMBS_UP: "👍",
  THUMBS_DOWN: "👎",
  LAUGH: "😄",
  HOORAY: "🎉",
  CONFUSED: "😕",
  HEART: "❤️",
  ROCKET: "🚀",
  EYES: "👀",
};

/** The spoken names GitHub uses in its own hover text, which is what a screen reader reads out. */
const REACTION_NAME: Record<PullRequestReactionContent, string> = {
  THUMBS_UP: "thumbs up",
  THUMBS_DOWN: "thumbs down",
  LAUGH: "laugh",
  HOORAY: "hooray",
  CONFUSED: "confused",
  HEART: "heart",
  ROCKET: "rocket",
  EYES: "eyes",
};

export function pullRequestReactionEmoji(content: PullRequestReactionContent): string {
  return REACTION_EMOJI[content];
}

export function pullRequestReactionName(content: PullRequestReactionContent): string {
  return REACTION_NAME[content];
}

/**
 * Who reacted, as far as this Environment can say. The host tells us how many and whether the
 * viewer is among them, but not who the others are, so the sentence counts them rather than
 * naming people it does not know.
 */
export function pullRequestReactionTooltip(reaction: PullRequestReaction): string {
  const name = pullRequestReactionName(reaction.content);
  const others = reaction.viewerHasReacted ? reaction.count - 1 : reaction.count;
  if (reaction.viewerHasReacted && others <= 0) return `You reacted with ${name} emoji`;
  const people = `${others} ${others === 1 ? "person" : "people"}`;
  return reaction.viewerHasReacted
    ? `You and ${others} ${others === 1 ? "other" : "others"} reacted with ${name} emoji`
    : `${people} reacted with ${name} emoji`;
}

/**
 * The pills as they should be drawn while a reaction is still in flight. Reading the pull
 * request back from the host takes seconds, and a pill that does not move until then reads as a
 * press that did nothing.
 */
export function applyPendingPullRequestReactions(
  reactions: ReadonlyArray<PullRequestReaction>,
  pending: ReadonlyMap<PullRequestReactionContent, boolean>,
): ReadonlyArray<PullRequestReaction> {
  if (pending.size === 0) return reactions;
  const byContent = new Map(reactions.map((reaction) => [reaction.content, reaction] as const));
  for (const [content, reacted] of pending) {
    const current = byContent.get(content);
    if (current === undefined) {
      if (reacted) byContent.set(content, { content, count: 1, viewerHasReacted: true });
      continue;
    }
    if (current.viewerHasReacted === reacted) continue;
    const count = current.count + (reacted ? 1 : -1);
    // A reaction nobody is left holding is not a pill with a zero on it.
    if (count <= 0) byContent.delete(content);
    else byContent.set(content, { ...current, count, viewerHasReacted: reacted });
  }
  return PULL_REQUEST_REACTION_ORDER.flatMap((content) => byContent.get(content) ?? []);
}
